import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import {
  DetailedOffer,
  type DetailedOffer as DetailedOfferData,
} from '../../PrivateOfferForm/DetailedOffer/DetailedOffer'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ResaleAuthorizationCreation } from '../ResaleAuthorizationCreation/ResaleAuthorizationCreation'
import { debounce, isEmpty } from 'lodash'
import { getProducts } from '../../../modules/productsListing/action'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from '../../../store'
import { Product } from '../../../modules/productsListing/types'
import { flyoutFormDebounceTime } from '../../../common/utils/constants'
import { flyoutResaleSingleFieldFormDebounceTime } from '../constants'
import { KeyValue } from '../../../modules/privateOffer/privateOfferCreation/types'
import { useStyles } from './EditResaleAuthorizationWrapper.styles'
import { ResaleAuthorizationWithErrors } from '../../../modules/resaleAuthorization/types'
import {
  addRowsOnResaleAuthorization,
  deleteRowsOnResaleAuthorization,
  getCurrentResaleAuthorizationInCreation,
  updateDimensionOnResale,
  updatePricesOnResaleDimensions,
  updateResaleAuthorizationInCreation,
  updatevaluesInArrayOfResaleAuthorizations,
  sendResaleAuthorization,
  clearResaleAuthorizationInCreation,
} from '../../../modules/resaleAuthorization/action'
import { Dimension, PriceTypes } from '../../../types/flyout'
import {
  ButtonV2,
  ModalConfirm,
  ModalSizeVariants,
} from '@labrav/react-components'
import { fileUploadResaleAuthorization } from './fileUploadResaleAuthorization'
import { getResellersData } from '../../../modules/reseller/action'
import {
  getIsCustomPricing,
  getResaleOfferForDetailedView,
} from '../../../admin/components/ResaleAuthorizationsList/helpers'
import { ResaleAuthorizationsResellerAgreements } from '../../../admin/modules/resaleAuthorizations'
import { useAccountId } from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../../common/utils/hooks/useSyncFlyoutRouteParams'
import { useResaleAuthorizationSendAndDraftDisabled } from '../../../common/utils/hooks/resaleAuthorizationSentDisabled'
import {
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
} from '@labrav/common'
import type { EditResaleAuthorizationWrapperProps } from '../types'
import type { RootState } from '../../../store/types'

export type { EditResaleAuthorizationWrapperProps }

export const EditResaleAuthorizationWrapper: React.FC<
  EditResaleAuthorizationWrapperProps
> = ({ externalBaseURL, search, crmOpportunityId, crmObjectLink }) => {
  // Sync route params to selectedAccountIds for FLYOUT
  useSyncFlyoutRouteParams(!!externalBaseURL)

  const urlSearchParams = new URLSearchParams(search)
  const dispatch = useDispatch()
  const { classes } = useStyles()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    productId: productIdParam,
    resaleAuthorizationId,
    secureKey: secureKeyFromURL,
    partnerId: partnerIdFromURL,
  } = useParams<{
    cloud: string
    accountId: string
    productId: string
    resaleAuthorizationId: string
    secureKey?: string
    partnerId?: string
  }>()
  const productId = productIdParam as string

  const [selectedEulaFiles, setSelectedEulaFiles] = useState<File[]>([])
  const [selectedResellerAgreementFiles, setSelectedResellerAgreementFiels] =
    useState<File[]>([])

  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const resaleAuthorization = useSelector(
    (state: RootState) =>
      state.resaleAuthorizationsInCreation.currentResaleAuthorizationInCreation[
        productId
      ] || ({} as ResaleAuthorizationWithErrors)
  )
  const product =
    products.find(product => product.productId === productId) || ({} as Product)

  const resellers = useSelector((state: RootState) => state.resellers.resellers)

  const loading = useSelector((state: RootState) =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const resellersLoading = useSelector((state: RootState) =>
    isLoading(state.loading)(LoadingTypes.RESELLERS_DATA)
  )
  const setEulaFile = useCallback(
    (files: File[]) => {
      setSelectedEulaFiles(files)
    },
    [selectedEulaFiles, productId]
  )

  const setResellerAgreementFile = useCallback(
    (files: File[]) => {
      setSelectedResellerAgreementFiels(files)
    },
    [selectedResellerAgreementFiles, productId]
  )

  const { sendDisabled, draftDisabled } =
    useResaleAuthorizationSendAndDraftDisabled(
      productId,
      selectedEulaFiles,
      selectedResellerAgreementFiles
    )

  const [openModal, setOpenModal] = useState<boolean>(false)
  const [offerData, setOfferData] = useState<DetailedOfferData>(
    {} as DetailedOfferData
  )
  const timeZone = useSelector(
    (state: RootState) => state.userProfile.userProfile.defaultTimeZone || 'UTC'
  )
  const handleOpenModal = useCallback(async () => {
    const offer = getResaleOfferForDetailedView(
      {
        ...resaleAuthorization,
        products: [
          {
            awsProductId: productId,
            dimensions: resaleAuthorization.dimensions || [],
          },
        ],
      } as ResaleAuthorizationsResellerAgreements,
      timeZone
    )
    setOfferData(offer)
    setOpenModal(true)
  }, [resaleAuthorization, product, productId, timeZone])

  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [openModal])
  const handleSentToBuyer = useCallback(
    (saveAsDraft: boolean) => async () => {
      setOpenModal(false)
      if (
        !isEmpty(selectedEulaFiles) ||
        !isEmpty(selectedResellerAgreementFiles)
      ) {
        await dispatch(
          fileUploadResaleAuthorization({
            partnerId: accountId,
            productId,
            saveAsDraft,
            crmOpportunityId,
            crmObjectLink,
            selectedEulaFiles,
            selectedResellerAgreementFiles,
          })
        )
        setSelectedEulaFiles([])
        setSelectedResellerAgreementFiels([])
      } else {
        const creatorEmail = urlSearchParams?.get('creator_email') || ''
        const creatorName = urlSearchParams?.get('creator_name') || ''
        const hasCreatorData = !!externalBaseURL && creatorEmail && creatorName
        await dispatch(
          sendResaleAuthorization({
            partnerId: accountId,
            productId,
            saveAsDraft,
            crmOpportunityId,
            crmObjectLink,
            ...(hasCreatorData
              ? {
                  creatorData: {
                    creatorEmail,
                    creatorName,
                  },
                }
              : {}),
          })
        )
      }
    },
    [
      resaleAuthorization,
      product,
      productId,
      selectedEulaFiles,
      selectedResellerAgreementFiles,
      urlSearchParams,
    ]
  )
  const baseURL = useMemo(
    () =>
      externalBaseURL
        ? routeCloud && accountId
          ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${accountId}/product-listing/${productId}`
          : externalBaseURL
        : `/flyout/${routeCloud}/${accountId}/products-listing/${productId}`,
    [externalBaseURL, productId, routeCloud, accountId, secureKeyFromURL]
  )
  const navigate = useNavigate()
  useEffect(() => {
    if (resaleAuthorization.hasBeenSent && !isEmpty(resaleAuthorization.id)) {
      const resaleAuthorizationId = resaleAuthorization.id
      navigate(
        {
          pathname: `${baseURL}/resale-authorizations/${resaleAuthorizationId}/details`,
          search,
        },
        { replace: true }
      )
      return () => {
        dispatch(clearResaleAuthorizationInCreation(productId))
      }
    } else if (
      !isEmpty(resaleAuthorization.id) &&
      resaleAuthorization.noOfTimesSaved === 1
    ) {
      navigate(
        {
          pathname: `${baseURL}/resale-authorizations/${resaleAuthorization.id}/edit`,
          search,
        },
        { replace: true }
      )
    }
  }, [
    resaleAuthorization.id,
    resaleAuthorization.hasBeenSent,
    resaleAuthorization.noOfTimesSaved,
  ])
  useEffect(() => {
    if (!isEmpty(accountId)) {
      {
        if (isEmpty(products)) {
          dispatch(getProducts(accountId, selectedCloudTypeProductListing))
        } else {
          dispatch(
            getCurrentResaleAuthorizationInCreation({
              partnerId: accountId,
              productId,
              resaleAuthorizationId: resaleAuthorizationId || '',
              crmOpportunityId,
            })
          )
          if (isEmpty(resellers) && !resellersLoading) {
            dispatch(getResellersData(accountId))
          }
        }
      }
    }
  }, [accountId, products, productId, resaleAuthorizationId, crmOpportunityId])

  const updateValuesInArray = useCallback(
    (index: number, key: string, type?: string) =>
      debounce(async (data: KeyValue) => {
        await dispatch(
          updatevaluesInArrayOfResaleAuthorizations(key, index, data, productId)
        )
      }, flyoutFormDebounceTime),
    [productId]
  )

  const addRow = useCallback(
    async (key: string, type = 'standard') => {
      await dispatch(addRowsOnResaleAuthorization(key, productId, type))
    },
    [productId]
  )

  const handleChange = useCallback(
    debounce(async (data: KeyValue) => {
      await dispatch(
        updateResaleAuthorizationInCreation(
          {
            key: data.key,
            value: data.value,
          },
          productId
        )
      )
    }, flyoutResaleSingleFieldFormDebounceTime),
    [productId]
  )

  const deleteRow = useCallback(
    (index: number, key: string) => async () => {
      await dispatch(deleteRowsOnResaleAuthorization(key, index, productId))
    },
    [productId]
  )
  const updateDimension = useCallback(
    async (index: number, dimension: Dimension) => {
      await dispatch(updateDimensionOnResale(index, productId, dimension))
    },
    [productId]
  )

  const updatePricesOnDimensions = useCallback(
    async (index: number, priceData: KeyValue) => {
      await dispatch(
        updatePricesOnResaleDimensions(productId, index, priceData)
      )
    },
    [productId]
  )

  const canShowResaleAuthorization =
    !isEmpty(accountId) &&
    !isEmpty(resaleAuthorization) &&
    !isEmpty(product) &&
    resaleAuthorization.draft &&
    !isEmpty(resellers) &&
    !resellersLoading

  const isCustomPricing = getIsCustomPricing(
    resaleAuthorization?.discountType,
    resaleAuthorization?.contractDurationType
  )
  const pricesArray = (
    isCustomPricing ? ['price'] : resaleAuthorization?.pricesArray || []
  ) as [keyof PriceTypes]
  return canShowResaleAuthorization ? (
    <div className={classes.root} data-testid="resale-authorization-creation">
      <ResaleAuthorizationCreation
        resaleAuthorization={resaleAuthorization}
        updateValuesInArray={updateValuesInArray}
        addRow={addRow}
        deleteRow={deleteRow}
        handleChange={handleChange}
        selectedEulaFiles={selectedEulaFiles}
        setEulaFile={setEulaFile}
        selectedResellerAgreementFiles={selectedResellerAgreementFiles}
        setResellerAgreementFile={setResellerAgreementFile}
        product={product}
        updateDimension={updateDimension}
        updatePricesOnDimensions={updatePricesOnDimensions}
      />
      <div className={classes.buttonContainer}>
        <ButtonV2
          styleType="outlined"
          onClick={handleOpenModal}
          disabled={loading || draftDisabled}
          data-testid="save-button-resale"
        >
          {'Save and preview'}
        </ButtonV2>
        <ButtonV2
          styleType="primary"
          onClick={handleSentToBuyer(false)}
          disabled={sendDisabled || loading}
        >
          {'Send to reseller'}
        </ButtonV2>
      </div>
      <ModalConfirm
        title={'Preview'}
        subTitle={''}
        content={<DetailedOffer offer={offerData} pricesArray={pricesArray} />}
        open={openModal}
        titleAcceptButton={'Okay'}
        onAccept={handleSentToBuyer(true)}
        onDecline={handleClose}
        variant={ModalSizeVariants.medium}
        hideCancelButton={true}
      />
    </div>
  ) : (
    <div data-testid="empty-div-resale-authorization-creation"></div>
  )
}
